import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import { formatFullDateTime12h } from '../../utils/timeFormat';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import {
  ArrowLeft, Search, Activity, User, FileEdit, Upload, Trash2,
  FileCheck, Settings, Shield, Clock, Loader2, ChevronLeft, ChevronRight,
  UserPlus, LogIn, AlertTriangle, ShieldAlert, ExternalLink, Mail, Phone
} from 'lucide-react';
import { toast } from 'sonner';

const ActivityLog = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [actionFilter, setActionFilter] = useState('all');
  const [view, setView] = useState('operational'); // Iter-227d — operational | security | all
  const [availableActions, setAvailableActions] = useState([]);
  const [userFilter, setUserFilter] = useState(searchParams.get('user_id') || '');
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const limit = 50;

  useEffect(() => {
    fetchActions();
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [page, actionFilter, userFilter, view]);

  const fetchActions = async () => {
    try {
      const res = await api.get('/admin/activity-logs/actions');
      setAvailableActions(res.data || []);
    } catch {}
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', limit);
      params.append('offset', page * limit);
      params.append('view', view);
      if (actionFilter !== 'all') params.append('action', actionFilter);
      if (userFilter) params.append('user_id', userFilter);
      
      const res = await api.get(`/admin/activity-logs?${params.toString()}`);
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      toast.error('Failed to load activity logs');
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    if (action?.includes('profile') || action?.includes('update')) return <FileEdit className="w-4 h-4" />;
    if (action?.includes('upload')) return <Upload className="w-4 h-4" />;
    if (action?.includes('delete')) return <Trash2 className="w-4 h-4" />;
    if (action?.includes('contract') || action?.includes('sign')) return <FileCheck className="w-4 h-4" />;
    if (action?.includes('login') || action?.includes('impersonat')) return <LogIn className="w-4 h-4" />;
    if (action?.includes('admin')) return <Shield className="w-4 h-4" />;
    if (action?.includes('user') || action?.includes('register')) return <UserPlus className="w-4 h-4" />;
    return <Activity className="w-4 h-4" />;
  };

  const getActionColor = (action) => {
    if (action?.includes('delete') || action?.includes('suspend')) return 'bg-red-100 text-red-800';
    if (action?.includes('create') || action?.includes('register')) return 'bg-green-100 text-green-800';
    if (action?.includes('update') || action?.includes('profile')) return 'bg-blue-100 text-blue-800';
    if (action?.includes('admin') || action?.includes('impersonat')) return 'bg-purple-100 text-purple-800';
    if (action?.includes('sign') || action?.includes('contract')) return 'bg-amber-100 text-amber-800';
    return 'bg-gray-100 text-gray-800';
  };

  const formatAction = (action) => {
    return action?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown';
  };

  const formatDate = (dateStr) => {
    return formatFullDateTime12h(dateStr);
  };

  const openDetail = async (log) => {
    // Show what we have immediately, then enrich with the detail endpoint.
    setSelectedLog({ ...log, _loading: true });
    setDetailLoading(true);
    try {
      const res = await api.get(`/admin/activity-logs/${log.id}`);
      setSelectedLog(res.data);
    } catch {
      setSelectedLog({ ...log, _loading: false });
      toast.error('Failed to load full details');
    } finally {
      setDetailLoading(false);
    }
  };

  const navigateToEntity = (et, eid) => {
    if (!et || !eid) return;
    setSelectedLog(null);
    if (et === 'user') navigate(`/admin/users/${eid}`);
    else if (et === 'shift') navigate(`/admin/shifts/${eid}`);
    else if (et === 'booking') navigate(`/admin/bookings/${eid}`);
    else if (et === 'invoice') navigate(`/admin/invoicing/${eid}`);
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="activity-log-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/home')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <Activity className="w-5 h-5 text-cyan-500" />
              {view === 'security' ? 'Security Audit' : 'Activity Log'}
            </h1>
          </div>
          <Badge className="bg-gray-100 text-gray-700">{total} total entries</Badge>
        </div>
        {/* Iter-227d — view toggle */}
        <div className="max-w-7xl mx-auto px-4 pb-3 flex gap-1.5" data-testid="activity-view-toggle">
          {[
            { code: 'operational', label: 'Operational', icon: Activity, hint: 'Hides admin impersonation noise' },
            { code: 'security',    label: 'Security Audit', icon: ShieldAlert, hint: 'Impersonation + role changes (super-admin only)' },
            { code: 'all',         label: 'All', icon: Shield, hint: 'Legacy unfiltered feed' },
          ].map(v => (
            <button
              key={v.code}
              type="button"
              title={v.hint}
              onClick={() => { setView(v.code); setPage(0); }}
              data-testid={`activity-view-${v.code}`}
              className={`text-xs uppercase tracking-wider px-3 py-1.5 rounded-md font-semibold border flex items-center gap-1.5 ${view === v.code ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}
            >
              <v.icon className="w-3.5 h-3.5" />
              {v.label}
            </button>
          ))}
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Filter by user ID..."
                  value={userFilter}
                  onChange={(e) => {
                    setUserFilter(e.target.value);
                    setPage(0);
                  }}
                  className="pl-10"
                  data-testid="user-filter-input"
                />
              </div>
              <Select 
                value={actionFilter} 
                onValueChange={(val) => {
                  setActionFilter(val);
                  setPage(0);
                }}
              >
                <SelectTrigger className="w-56" data-testid="action-filter">
                  <SelectValue placeholder="Filter by action" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  {availableActions.map(action => (
                    <SelectItem key={action} value={action}>
                      {formatAction(action)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Activity List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
          </div>
        ) : logs.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-gray-500">
              <Activity className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No activity logs found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {logs.map((log) => {
              // Iter-227j — Pre-compute the "target entity" label so the row
              // shows WHO/WHAT was affected at a glance.
              const targetSummary = log.entity_type && log.entity_id
                ? `${log.entity_type} · ${String(log.entity_id).slice(0, 8)}…`
                : null;
              const category = (() => {
                const a = log.action || '';
                if (a.includes('invoice') || a.includes('payment') || a.includes('payout')) return 'Billing';
                if (a.includes('idv') || a.includes('contract') || a.includes('cert')) return 'Compliance';
                if (a.includes('impersonat') || a.includes('login')) return 'Security';
                if (a.includes('booking') || a.includes('shift') || a.includes('clock')) return 'Operations';
                if (a.includes('user') || a.includes('register') || a.includes('profile')) return 'Accounts';
                if (a.includes('admin')) return 'Admin';
                return 'Other';
              })();
              return (
                <Card
                  key={log.id}
                  className="hover:shadow-md hover:border-cyan-300 transition-all cursor-pointer border-gray-200"
                  data-testid="activity-log-entry"
                  onClick={() => openDetail(log)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openDetail(log); } }}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${getActionColor(log.action)}`}>
                        {getActionIcon(log.action)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={getActionColor(log.action) + ' font-semibold'}>
                            {formatAction(log.action)}
                          </Badge>
                          <Badge variant="outline" className="text-[10px] uppercase tracking-wider text-gray-500 border-gray-300">
                            {category}
                          </Badge>
                          {log.entity_type && (
                            <Badge variant="outline" className="text-xs border-cyan-200 text-cyan-700 bg-cyan-50">
                              Affects: {log.entity_type}
                            </Badge>
                          )}
                        </div>
                        <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 text-xs">
                          <div className="flex items-center gap-1.5 text-gray-700 min-w-0">
                            <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold shrink-0">Admin:</span>
                            <User className="w-3 h-3 text-gray-400 shrink-0" />
                            <span className="font-medium truncate">{log.user_name || 'System'}</span>
                            {log.user_email && <span className="text-gray-400 truncate">({log.user_email})</span>}
                          </div>
                          <div className="flex items-center gap-1.5 text-gray-700 min-w-0">
                            <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold shrink-0">Target:</span>
                            <span className="font-medium truncate">{targetSummary || <span className="text-gray-400">—</span>}</span>
                          </div>
                        </div>
                        {log.details && Object.keys(log.details).length > 0 && (
                          <p className="mt-1.5 text-xs text-gray-500 truncate">
                            {JSON.stringify(log.details).slice(0, 110)}
                          </p>
                        )}
                      </div>
                      <div className="text-right text-xs text-gray-400 flex flex-col items-end gap-1 shrink-0">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(log.created_at)}
                        </span>
                        <span className="text-[10px] uppercase tracking-wider text-cyan-600 font-bold">View details →</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Iter-227i — Activity Log drill-down dialog */}
        <Dialog open={!!selectedLog} onOpenChange={(o) => { if (!o) setSelectedLog(null); }}>
          <DialogContent className="max-w-2xl" data-testid="activity-log-detail-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {selectedLog && (
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getActionColor(selectedLog.action)}`}>
                    {getActionIcon(selectedLog.action)}
                  </div>
                )}
                <span>{selectedLog ? formatAction(selectedLog.action) : ''}</span>
              </DialogTitle>
              <DialogDescription>
                {selectedLog ? formatDate(selectedLog.created_at) : ''}
              </DialogDescription>
            </DialogHeader>

            {selectedLog && (
              <div className="space-y-4 text-sm">
                {/* Actor */}
                <div className="bg-gray-50 rounded-md p-3 border border-gray-200">
                  <p className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Actor</p>
                  <p className="font-semibold text-gray-900">{selectedLog.user_name || 'System'}</p>
                  {selectedLog.user_email && (
                    <p className="text-xs text-gray-600 flex items-center gap-1 mt-0.5">
                      <Mail className="w-3 h-3" /> {selectedLog.user_email}
                      {selectedLog.user_role && <span className="text-gray-400">· {selectedLog.user_role}</span>}
                    </p>
                  )}
                  {selectedLog.user_id && (
                    <button
                      className="text-xs text-cyan-600 hover:text-cyan-700 mt-1 flex items-center gap-1"
                      onClick={() => navigateToEntity('user', selectedLog.user_id)}
                      data-testid="goto-actor-btn"
                    >
                      View actor profile <ExternalLink className="w-3 h-3" />
                    </button>
                  )}
                </div>

                {/* Entity */}
                {(selectedLog.entity_type || selectedLog.entity_id) && (
                  <div className="bg-cyan-50/50 rounded-md p-3 border border-cyan-100">
                    <p className="text-[11px] uppercase tracking-wider text-cyan-700 font-semibold mb-1">
                      Target {selectedLog.entity_type || ''}
                    </p>
                    {selectedLog.entity_summary?.label && (
                      <p className="font-semibold text-gray-900">{selectedLog.entity_summary.label}</p>
                    )}
                    {selectedLog.entity_summary && (
                      <div className="text-xs text-gray-600 mt-0.5 space-y-0.5">
                        {Object.entries(selectedLog.entity_summary)
                          .filter(([k]) => k !== 'label')
                          .map(([k, v]) => (
                            <p key={k}><span className="text-gray-400 capitalize">{k.replace('_', ' ')}:</span> {String(v)}</p>
                          ))}
                      </div>
                    )}
                    {selectedLog.entity_id && (
                      <p className="text-[10px] text-gray-400 mt-1 font-mono">id: {selectedLog.entity_id}</p>
                    )}
                    {selectedLog.entity_type && selectedLog.entity_id && (
                      <button
                        className="text-xs text-cyan-700 hover:text-cyan-800 mt-1 flex items-center gap-1 font-semibold"
                        onClick={() => navigateToEntity(selectedLog.entity_type, selectedLog.entity_id)}
                        data-testid="goto-entity-btn"
                      >
                        Open this {selectedLog.entity_type} <ExternalLink className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                )}

                {/* Details JSON */}
                {selectedLog.details && Object.keys(selectedLog.details).length > 0 && (
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Details</p>
                    <pre
                      className="bg-gray-900 text-emerald-100 text-[11px] rounded-md p-3 overflow-auto max-h-64 font-mono"
                      data-testid="activity-log-detail-json"
                    >{JSON.stringify(selectedLog.details, null, 2)}</pre>
                  </div>
                )}

                {/* Request context — always rendered so super-admins see
                    the audit fields even when both are null. */}
                <div className="text-xs text-gray-500 space-y-0.5 border-t border-gray-100 pt-3" data-testid="activity-log-request-context">
                  <p>
                    <span className="text-gray-400">IP:</span>{' '}
                    <span className="font-mono">{selectedLog.ip_address || <span className="text-gray-300">—</span>}</span>
                  </p>
                  <p className="truncate" title={selectedLog.user_agent || ''}>
                    <span className="text-gray-400">User Agent:</span>{' '}
                    {selectedLog.user_agent || <span className="text-gray-300">—</span>}
                  </p>
                </div>

                {detailLoading && (
                  <p className="text-xs text-gray-400 italic flex items-center gap-1">
                    <Loader2 className="w-3 h-3 animate-spin" /> Loading full details...
                  </p>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6">
            <p className="text-sm text-gray-500">
              Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-gray-600">
                Page {page + 1} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityLog;
